import { Fragment, useState, useMemo } from "react"
import { useShallow } from "zustand/react/shallow"
import { useInView } from "react-intersection-observer"

// components
import { Affix } from "antd"
import MeasureCategory from "../MeasureCategory/MeasureCategory"
import { TextInput, Select } from "components-design-system"
import Label from "components/Label/Label"
import RowActions from "../../../components/RowActions/RowActions"
import CharCounter from "components/CharCounter/CharCounter"
import CellValidation from "../../../components/CellValidation/CellValidation"

// context
import useResultsTableViewStore from "containers/studies/Study/subcomponents/Authoring/hooks/stores/useResultsTableViewStore"
import useAuthoringViewStore from "containers/studies/Study/subcomponents/Authoring/hooks/stores/useAuthoringViewStore"

// enum
import { COUNTRIES } from "../../../../schemas/dataSchemas/enum/enum"
import "./MeasureRow.scss"

const MeasureRow = ({
  rows,
  rowIndex,
  schema,
  bmSchema,
  measure,
  measureIndex,
  row,
  baselineData,
  setBaselineData,
  scrollXPosition,
  isSticky,
  canAddCategory,
  actionsRegistrySpecific,
}) => {
  const { ref: visibleRef } = useInView({
    /* Optional options */
    threshold: 0,
    triggerOnce: true,
  })

  const [showActions, setShowActions] = useState(false)

  const { tableView } = useResultsTableViewStore()

  const { readOnly, fullScreen, formView } = useAuthoringViewStore(
    useShallow((state) => ({
      readOnly: state.readOnly,
      fullScreen: state.fullScreen,
      formView: state.formView,
    }))
  )

  const canEditRows = useMemo(() => {
    return !actionsRegistrySpecific("canEditRowTitle")
  }, [row])

  const handleTitle = (e, oldVal) => {
    let tempData = { ...baselineData }

    Object.keys(tempData).forEach((registry) => {
      let foundBMIndex = tempData[registry].baseline_measures.findIndex((bm) => bm.id === measure.id)
      let bm = tempData[registry].baseline_measures[foundBMIndex]

      if (foundBMIndex != -1) {
        let foundRowIndex = bm.rows.findIndex((bmRow) => bmRow.id === row.id)
        if (foundRowIndex !== -1) {
          if (canEditRows === true || registry === tableView) bm.rows[foundRowIndex].title = e
        }
      }
    })

    setBaselineData(tempData)
  }

  const handleDeleteRow = (applyAll) => {
    let tempData = { ...baselineData }

    Object.keys(tempData).forEach((registry) => {
      if (applyAll || registry === tableView) {
        let foundBMIndex = tempData[registry].baseline_measures.findIndex((bm) => bm.id === measure.id)
        let bm = tempData[registry].baseline_measures[foundBMIndex]

        if (foundBMIndex != -1) {
          let foundRowIndex = bm.rows.findIndex((bmRow) => bmRow.id === row.id)
          if (foundRowIndex !== -1) {
            bm.rows.splice(foundRowIndex, 1)
          }
        }
      }
    })

    setBaselineData(tempData)
  }

  const displayRowActions = () => {
    let colSpan = 1

    return (
      <tr
        onMouseEnter={() => setShowActions(true)}
        onMouseLeave={() => setShowActions(false)}
        ref={visibleRef}
      >
        <td
          className="col-sm"
          colSpan={colSpan}
          rowSpan={row.categories.length + 1}
        >
          {displayRowTitle()}
          <div className="bc-arm-actions">
            {formView?.id !== "global" && tableView === "ctg" && !readOnly && (
              <div className="char-counter-container">
                <CharCounter
                  value={row.title || ""}
                  maxChars={50}
                />
              </div>
            )}
            <RowActions
              showActions={showActions}
              setShowActions={setShowActions}
              handleDelete={handleDeleteRow}
              canDelete={measure.rows.length > 1}
              deleteModalTitle="Delete row?"
              jpath={`BaselineReportingGroup.bc-${measure.id}-row-${row.id}`}
            />
          </div>
          <CellValidation
            table="baseline"
            errorKey={`results_baseline_characteristics_baseline_characteristics_table_${tableView}_baseline_measures_${
              measureIndex + 1
            }_rows_${rowIndex + 1}_title`}
          />
        </td>
      </tr>
    )
  }

  const displayRowTitle = () => {
    if (measure.title === "Region of Enrollment") {
      return (
        <Select
          label="Row title"
          options={COUNTRIES}
          readOnly={readOnly || bmSchema.canEditRowTitle === false}
          value={row.title}
          onChange={handleTitle}
        />
      )
    } else {
      return (
        <TextInput
          type="textarea"
          label={"Row title"}
          disabled={bmSchema.canEditRowTitle === false}
          readOnly={readOnly}
          tooltip={
            tableView === "eudract" &&
            bmSchema.showEditTitlesTooltip !== false &&
            "Note: Row titles will be combined with Category Titles for EudraCT submissions. e.g. Row 1 Category 1, Row 1 Category 2, Row 2 Category 1, Row 2 Category 2, etc..."
          }
          value={row.title}
          onChange={(e) => handleTitle(e.target.value)}
        />
      )
    }
  }

  const displayGroupHeader = () => {
    if (fullScreen && measureIndex !== 0) return null

    const findGroup = (group) => {
      let foundGroup = null
      foundGroup = baselineData[tableView]?.subject_analysis_sets?.find((sas) => sas.id === group.id)

      if (foundGroup) {
        // SAS
        return (
          <div
            className="sas-header measure-group-header column-header"
            key={group.id}
          >
            <Label>Group title: {group?.title}</Label>
            <Label>Number of Subjects: {group?.baseline_analysis_population}</Label>
          </div>
        )
      } else {
        // ARM
        foundGroup = baselineData[tableView]?.arms?.find((arm) => arm.id === group.id)
        if (foundGroup) {
          return (
            <div
              className="measure-group-header column-header"
              key={group.id}
            >
              <Label>Group title: {group?.title}</Label>
              <Label>Number of Subjects: {group?.baseline_analysis_population}</Label>
            </div>
          )
        }
      }
    }

    let totalArmParticipants = baselineData[tableView]?.arms?.reduce((acc, group) => {
      if (group.baseline_analysis_population) {
        return acc + Number(group.baseline_analysis_population)
      } else {
        return acc
      }
    }, 0)

    if (isNaN(totalArmParticipants)) totalArmParticipants = 0

    let totalSasParticipants = baselineData[tableView]?.subject_analysis_sets?.reduce((acc, group) => {
      if (group.baseline_analysis_population) {
        return acc + Number(group.baseline_analysis_population)
      } else {
        return acc
      }
    }, 0)

    if (isNaN(totalSasParticipants)) totalSasParticipants = 0

    const groupsToDisplay = []

    if (baselineData?.[tableView]?.arms?.length > 0) {
      baselineData[tableView].arms.forEach((group) => {
        groupsToDisplay.push(group)
      })
    }
    if (baselineData?.[tableView]?.subject_analysis_sets?.length > 0) {
      baselineData[tableView].subject_analysis_sets.forEach((group) => {
        groupsToDisplay.push({ ...group, is_subject_analysis_set: true })
      })
    }

    return (
      <tr>
        <td
          className="header-column"
          colSpan={2}
          style={{ paddingBottom: 25.5 }}
        >
          Groups
        </td>
        {fullScreen && isSticky
          ? groupsToDisplay.map((value, i) => {
              return (
                <td
                  className={value?.is_subject_analysis_set ? "sas-header" : ""}
                  style={{ padding: 0, width: 250 }}
                >
                  <Affix
                    target={() => document.getElementById("authoring-form-container")}
                    className="affix-results-row"
                    style={{
                      position: "absolute",
                      left: 377.5 - scrollXPosition + i * 250,
                      width: 250,
                      borderRight: "1px solid #adb0b4",
                    }}
                  >
                    {findGroup(value)}
                  </Affix>
                  <div style={{ opacity: 0 }}>{findGroup(value)}</div>
                </td>
              )
            })
          : groupsToDisplay.map((value) => {
              return (
                <td
                  className={value?.is_subject_analysis_set ? "sas-header" : ""}
                  style={{ padding: 0 }}
                >
                  {findGroup(value)}
                </td>
              )
            })}

        <td style={{ backgroundColor: "#f3f3f3" }}>
          <Label>Total Participants: {totalArmParticipants + totalSasParticipants}</Label>
        </td>
      </tr>
    )
  }

  const getRegionEnrollment = () => {
    // if (tableView === "eudract") {
    let foundCtgBaseline = baselineData?.ctg?.baseline_measures.find((bm) => bm.id === measure.id)
    if (foundCtgBaseline?.baseline_measure_id === "ctg-region-categorical") {
      return true
    }
    // }
    return false
  }

  return (
    <Fragment>
      {rowIndex === 0 && displayGroupHeader()}
      {displayRowActions()}
      {row.categories.map((category, catIndex) => {
        return (
          <tr key={`bm-${rowIndex}-${catIndex}`}>
            <MeasureCategory
              schema={schema}
              measure={measure}
              measureIndex={measureIndex}
              rowIndex={rowIndex}
              category={category}
              catIndex={catIndex}
              rows={rows}
              row={row}
              numOfCategories={row.categories.length}
              baselineData={baselineData}
              setBaselineData={setBaselineData}
              canAddCategory={canAddCategory}
            />
          </tr>
        )
      })}
    </Fragment>
  )
}

export default MeasureRow
